import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { SvgProps } from 'react-native-svg';

interface TrackableActionCardProps {
  id: number;
  name: string;
  icon: FC<SvgProps>;
  description: string;
  count: number;
  points?: number;
  alreadyDoneToday?: boolean;
  onPress: () => void;
  onInfoPress: () => void;
}

export default function TrackableActionCard({
  name,
  icon,
  count,
  points = 10,
  alreadyDoneToday = false,
  onPress,
  onInfoPress,
}: TrackableActionCardProps) {
  const { t } = useTranslation();
  const isSelected = count > 0;
  const isLocked = alreadyDoneToday;

  const handlePress = async () => {
    if (isLocked) return;
    try {
      const Haptics = await import('expo-haptics');
      if (isSelected) {
        await Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
      } else {
        await Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
      }
    } catch {
      // Haptics not available
    }
    onPress();
  };

  return (
    <TouchableOpacity
      style={[styles.actionCard, isLocked && styles.actionCardLocked]}
      onPress={handlePress}
      activeOpacity={isLocked ? 1 : 0.7}
      disabled={isLocked}
    >
      {(isSelected || isLocked) && (
        <LinearGradient
          colors={['rgba(131, 191, 79, 0.65)', 'rgba(131, 191, 79, 0.5)']}
          start={{ x: 0, y: 0.5 }}
          end={{ x: 1, y: 0.5 }}
          style={styles.actionCardGradient}
        />
      )}
      <LinearGradient
        colors={['#FCC189', '#CD7728']}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.pointsBadge}
      >
        <Text style={styles.pointsText}>+{points}</Text>
      </LinearGradient>
      <TouchableOpacity
        style={styles.infoBadge}
        onPress={(e) => {
          e.stopPropagation();
          onInfoPress();
        }}
        hitSlop={8}
        accessibilityRole="button"
        accessibilityLabel={t('common.moreInfo')}
      >
        <Ionicons name="information" size={18} color="#FFF" />
      </TouchableOpacity>
      <View style={styles.actionIconContainer}>
        {React.createElement(icon, { width: 40, height: 40 })}
      </View>
      <Text
        style={[styles.actionName, (isSelected || isLocked) && styles.actionNameOnGreen]}
        numberOfLines={2}
        adjustsFontSizeToFit
        minimumFontScale={0.5}
        ellipsizeMode="tail"
        allowFontScaling={false}
      >
        {name}
      </Text>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  actionCard: {
    width: '31%',
    aspectRatio: 1,
    backgroundColor: '#FFF',
    borderRadius: 12,
    padding: 12,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    overflow: 'hidden',
  },
  actionCardLocked: {
    opacity: 0.95,
  },
  actionCardGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 12,
    zIndex: 1,
  },
  pointsBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 2,
    zIndex: 5,
  },
  pointsText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: '#FFF',
  },
  infoBadge: {
    position: 'absolute',
    top: 6,
    left: 6,
    width: 28,
    height: 28,
    backgroundColor: '#CD7728',
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.2,
    shadowRadius: 2,
    elevation: 3,
  },
  actionIconContainer: {
    width: 36,
    height: 36,
    marginBottom: 8,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 5,
  },
  actionName: {
    fontFamily: 'Inter_700Bold',
    fontSize: 10,
    color: '#000',
    textAlign: 'center',
    zIndex: 5,
    marginTop: 4,
    paddingHorizontal: 2,
    lineHeight: 13,
  },
  actionNameOnGreen: {
    color: '#FFF',
  },
  checkBadge: {
    position: 'absolute',
    bottom: 6,
    right: 6,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#1C453F',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20,
  },
});
